import React, { useMemo, useState, useEffect } from "react";
import { AnalyticsData } from "../Constant";
import { TopItemsData } from "../store/merchantAnalyticsOverviewSlice";
import { ChartContainer } from "../styles";
import { CHART_COLORS } from "../Constant";

interface TopItemsChartProps {
  analyticsData: AnalyticsData;
  topItemsData?: TopItemsData | null;
  isDarkMode?: boolean;
  loading?: boolean;
}

const TopItemsChart: React.FC<TopItemsChartProps> = ({
  analyticsData,
  topItemsData,
  isDarkMode = false,
  loading = false,
}) => {
  const [ReactECharts, setReactECharts] = useState<any>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
    // Dynamic import to avoid SSR issues
    import("echarts-for-react").then((module) => {
      setReactECharts(() => module.default);
    });
  }, []);

  const option = useMemo(() => {
    const items = topItemsData?.items || [];    
    const data = items
      .filter((item) => item.name && (item.revenue || 0) > 0)
      .slice(0, 10)
      .sort((a, b) => (b.revenue || 0) - (a.revenue || 0))
      .map((item) => ({
        name: item.name || 'N/A',
        value: item.revenue || 0,
        categoryName: item.categoryName || 'N/A',
      }));

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "shadow",
        },
        backgroundColor: isDarkMode ? "#1a202c" : "#fff",
        borderColor: isDarkMode ? "#4a5568" : "#e2e8f0",
        textStyle: {
          color: isDarkMode ? "#fff" : "#1a202c",
        },
        formatter: (params: any) => {
          const param = Array.isArray(params) ? params[0] : params;
          if (!param || param.value === undefined || param.value === null) {
            return '';
          }
          const item = data.find((d) => d.name === param.name);
          return `${param.name || 'N/A'}<br/>Revenue: $${Number(param.value).toFixed(
            2
          )}<br/>Category: ${item?.categoryName || 'N/A'}`;
        },
      },
      grid: {
        left: "25%",
        right: "4%",
        bottom: "3%",
        containLabel: false,
      },
      xAxis: {
        type: "value",
        axisLine: {
          lineStyle: {
            color: isDarkMode ? "#4a5568" : "#d9d9d9",
          },
        },
        axisLabel: {
          color: isDarkMode ? "#a0aec0" : "#718096",
          formatter: (value: number) => `$${value.toLocaleString()}`,
        },
        splitLine: {
          lineStyle: {
            color: isDarkMode ? "#2d3748" : "#f0f0f0",
          },
        },
      },
      yAxis: {
        type: "category",
        data: data.map((item) => item.name),
        axisLine: {
          lineStyle: {
            color: isDarkMode ? "#4a5568" : "#d9d9d9",
          },
        },
        axisLabel: {
          color: isDarkMode ? "#a0aec0" : "#718096",
        },
      },
      series: [
        {
          name: "Revenue",
          type: "bar",
          barWidth: "60%",
          data: data.map((item, index) => ({
            value: item.value,
            itemStyle: {
              color: CHART_COLORS[index % CHART_COLORS.length],
              borderRadius: [0, 8, 8, 0],
            },
          })),
          emphasis: {
            focus: "series",
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: "rgba(0, 0, 0, 0.5)",
            },
          },
        },
      ],
    };
  }, [isDarkMode, topItemsData]);

  if (!isMounted || !ReactECharts || loading) {
    return (
      <ChartContainer>
        <div style={{ height: "100%", width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {!isMounted || !ReactECharts ? "Loading chart..." : "Loading top items data..."}
        </div>
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <ReactECharts option={option} style={{ height: "100%", width: "100%" }} />
    </ChartContainer>
  );
};

export default TopItemsChart;